import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Ship, Component, CompiledSystems } from '../../interfaces/types';
import { FaTimes, FaExchangeAlt, FaSave, FaTrash, FaSearchPlus } from 'react-icons/fa';
import { showToast } from '../common/ToastContainer';
import { ComponentLayout, PhysicalComponent, ShipSlot, garageService } from '../../services/garageService';
import { shipService } from '../../services/shipService';
import { buildPhysicalComponentSections } from '../../utils/physicalComponentDisplay';
import './ShipGarageRepair.css';

interface ShipGarageRepairProps {
  ship: Ship;
  onClose: () => void;
}

type GarageComponent = Component & {
  assigned_component?: PhysicalComponent;
};

type SlotLike = {
  size?: number | string;
  component_size?: number | string;
  sizeNumber?: number | string;
};

// Mapeo de nombres de categorías para mostrar en español
const CATEGORY_NAMES: Record<keyof CompiledSystems, string> = {
  RSIAvionic: 'Aviónica',
  RSIPropulsion: 'Propulsión',
  RSIThruster: 'Propulsores',
  RSIModular: 'Módulos',
  RSIWeapon: 'Armamento'
};

// Mapeo de nombres de tipos de componentes
const COMPONENT_TYPE_NAMES: Record<string, string> = {
  radar: 'Radar',
  computers: 'Computadoras',
  fuel_intakes: 'Tomas de Combustible',
  fuel_tanks: 'Tanques de Combustible',
  quantum_drives: 'Motores Cuánticos',
  jump_modules: 'Módulos de Salto',
  quantum_fuel_tanks: 'Tanques de Combustible Cuántico',
  main_thrusters: 'Propulsores Principales',
  maneuvering_thrusters: 'Propulsores de Maniobra',
  power_plants: 'Plantas de Energía',
  coolers: 'Refrigeradores',
  shield_generators: 'Generadores de Escudo',
  weapons: 'Armas',
  turrets: 'Torretas',
  missiles: 'Misiles',
  utility_items: 'Elementos de Utilidad'
};

// Mapeo de tipos de slot (catálogo) a tipo esperado por /componentes-fisicos?tipo=
const COMPONENT_TYPE_TO_API: Record<string, string> = {
  weapons: 'weapon',
  weapon: 'weapon',
  turrets: 'turret',
  turret: 'turret',
  missiles: 'missile',
  missile: 'missile',
  shield_generators: 'shield-generator',
  shield_generator: 'shield-generator',
  shields: 'shield-generator',
  shield: 'shield-generator',
  power_plants: 'power-plant',
  power_plant: 'power-plant',
  'power-plants': 'power-plant',
  'power-plant': 'power-plant',
  quantum_drives: 'quantum-drive',
  quantum_drive: 'quantum-drive',
  'quantum-drives': 'quantum-drive',
  radars: 'radar',
  radar: 'radar',
  coolers: 'cooler',
  cooler: 'cooler',
  computers: 'computer',
  computer: 'computer',
  fuel_tanks: 'fuel-tank',
  fuel_tank: 'fuel-tank',
  fuel_intakes: 'fuel-intake',
  fuel_intake: 'fuel-intake',
};

const normalizeComponentTypeForApi = (componentType: string): string | undefined => {
  const key = componentType.trim().toLowerCase();
  if (COMPONENT_TYPE_TO_API[key]) return COMPONENT_TYPE_TO_API[key];

  const normalizedKey = key.replace(/_/g, '-');
  if (COMPONENT_TYPE_TO_API[normalizedKey]) return COMPONENT_TYPE_TO_API[normalizedKey];

  return undefined;
};

const NON_REPLACEABLE_CATEGORIES = new Set([
  'fuel_intakes',
  'fuel_tanks',
  'quantum_fuel_tanks',
  'main_thrusters',
  'maneuvering_thrusters',
]);

export const ShipGarageRepair: React.FC<ShipGarageRepairProps> = ({ ship, onClose }) => {
  const [localShip, setLocalShip] = useState<Ship>(ship);
  const [shipDisplayName, setShipDisplayName] = useState(ship.nick || ship.name);
  const [layouts, setLayouts] = useState<ComponentLayout[]>([]);
  const [shipSlots, setShipSlots] = useState<ShipSlot[]>([]);
  const [savingNick, setSavingNick] = useState(false);
  const [layoutName, setLayoutName] = useState('');
  const [layoutDescription, setLayoutDescription] = useState('');
  const [isDefaultLayout, setIsDefaultLayout] = useState(false);
  const [editingLayoutId, setEditingLayoutId] = useState<string | null>(null);
  const [loadingLayouts, setLoadingLayouts] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  const [autoAppliedLayoutShipId, setAutoAppliedLayoutShipId] = useState<number | string | null>(null);

  const [selectorLoading, setSelectorLoading] = useState(false);
  const [selectorError, setSelectorError] = useState('');
  const [selectorOptions, setSelectorOptions] = useState<PhysicalComponent[]>([]);
  const [expandedComponentDetails, setExpandedComponentDetails] = useState<Record<string, boolean>>({});
  const [slotContext, setSlotContext] = useState<{
    categoryKey: string;
    typeKey: string;
    index: number;
    slot: SlotLike;
  } | null>(null);

  useEffect(() => {
    // Solo resetear estado local al cambiar de nave.
    // Evita perder asignaciones optimistas por re-renders del padre.
    setLocalShip(ship);
    setShipDisplayName(ship.nick || ship.name);
  }, [ship.id]);

  useEffect(() => {
    const loadLayouts = async () => {
      if (!localShip?.id) return;
      setLoadingLayouts(true);
      try {
        const response = await garageService.getShipLayouts(localShip.id);
        setLayouts(response);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'No se pudieron cargar los layouts';
        showToast(message, 'warning');
      } finally {
        setLoadingLayouts(false);
      }
    };

    loadLayouts();
  }, [localShip?.id]);

  useEffect(() => {
    const loadSlots = async () => {
      if (!localShip?.id) return;
      try {
        const response = await garageService.getShipSlots(localShip.id);
        setShipSlots(response);
      } catch {
        setShipSlots([]);
      }
    };
    loadSlots();
  }, [localShip?.id]);

  const getSlotSize = (component: SlotLike): number | string | undefined => {
    const withSizeNumber = component as SlotLike;

    // El backend de slots de catálogo expone sizeNumber; es la fuente preferida.
    if (withSizeNumber.sizeNumber !== undefined && withSizeNumber.sizeNumber !== null && `${withSizeNumber.sizeNumber}` !== '') {
      return withSizeNumber.sizeNumber;
    }

    const raw = component.size || component.component_size;
    if (raw === undefined || raw === null || `${raw}`.trim() === '') return undefined;

    if (typeof raw === 'number') return raw;

    const normalized = `${raw}`.trim().toLowerCase();
    if (/^\d+$/.test(normalized)) return Number(normalized);

    const sizeMap: Record<string, number> = {
      xs: 0,
      s: 0,
      m: 1,
      l: 2,
      xl: 3,
      capital: 4,
    };

    return sizeMap[normalized] ?? undefined;
  };

  const normalizeComparableSize = (value: unknown): string | null => {
    if (value === undefined || value === null) return null;
    const raw = `${value}`.trim().toLowerCase();
    if (!raw) return null;

    if (/^\d+$/.test(raw)) return raw;

    const sizeMap: Record<string, string> = {
      xs: '0',
      s: '0',
      m: '1',
      l: '2',
      xl: '3',
      capital: '4',
    };

    return sizeMap[raw] ?? raw;
  };

  const handleComponentChange = async (categoryKey: string, componentType: string, index: number, component: SlotLike) => {
    const apiType = normalizeComponentTypeForApi(componentType);
    if (!apiType) {
      showToast('Este tipo de slot no tiene endpoint de reemplazo todavía', 'warning');
      return;
    }

    setSlotContext({ categoryKey, typeKey: componentType, index, slot: component });
    setSelectorLoading(true);
    setSelectorError('');
    setSelectorOptions([]);

    try {
      const size = getSlotSize(component);
      const response = await garageService.getPhysicalComponents(apiType, size);
      const slotComparableSize = normalizeComparableSize(size);
      const sameSizeOnly = (response.componentes || []).filter((option) => {
        const optionComparableSize = normalizeComparableSize(option.size);
        if (!slotComparableSize || !optionComparableSize) return false;
        return optionComparableSize === slotComparableSize;
      });

      const ordered = [...sameSizeOnly].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
      );
      setSelectorOptions(ordered);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error cargando componentes';
      setSelectorError(message);
    } finally {
      setSelectorLoading(false);
    }
  };

  const closeSelector = () => {
    setSlotContext(null);
    setSelectorError('');
    setSelectorOptions([]);
  };

  const toggleComponentDetails = (key: string) => {
    setExpandedComponentDetails((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const asRecord = (value: unknown): Record<string, unknown> => {
    if (value && typeof value === 'object') return value as Record<string, unknown>;
    return {};
  };

  const getComponentSummaryValue = (component: PhysicalComponent, key: string): string | undefined => {
    const summary = asRecord(component.ui?.summary);
    const value = summary[key] ?? component[key];
    return value !== undefined && value !== null && `${value}` !== '' ? `${value}` : undefined;
  };

  const renderComponentDetailsPanel = (component: PhysicalComponent) => {
    const sections = buildPhysicalComponentSections(component);
    if (!sections.some((s) => s.rows.length > 0)) {
      return (
        <div className="component-details-panel">
          <div className="component-details-empty">No hay detalle adicional disponible.</div>
        </div>
      );
    }

    return (
      <div className="component-details-panel">
        {sections.map((section) =>
          section.rows.length === 0 ? null : (
            <div key={section.title} className="component-details-section">
              <h5>{section.title}</h5>
              <dl className="component-spec-dl">
                {section.rows.map((r) => (
                  <Fragment key={r.id}>
                    <dt>{r.label}</dt>
                    <dd>{r.value}</dd>
                  </Fragment>
                ))}
              </dl>
            </div>
          )
        )}
      </div>
    );
  };

  const currentLayoutSnapshot = useMemo(() => {
    const result: Record<string, Record<string, Array<Record<string, unknown>>>> = {};

    if (shipSlots.length > 0) {
      shipSlots.forEach((slot) => {
        const assigned = slot.assigned_component as unknown as Record<string, unknown>;
        if (!assigned) return;

        if (!result[slot.tipoComponente]) result[slot.tipoComponente] = {};
        if (!result[slot.tipoComponente][slot.categoria]) result[slot.tipoComponente][slot.categoria] = [];

        result[slot.tipoComponente][slot.categoria].push({
          slot: slot.slotIndex,
          assigned_component_ref: (assigned._id as string) || (assigned.id as string) || assigned.name,
          assigned_component: assigned,
        });
      });
      return result;
    }

    Object.entries(localShip.compiled || {}).forEach(([categoryKey, systems]) => {
      Object.entries(systems as unknown as Record<string, GarageComponent[]>).forEach(([typeKey, slots]) => {
        slots.forEach((slot, index) => {
          const assigned = (slot.assigned_component as unknown as Record<string, unknown>) || undefined;
          if (!assigned) return;

          if (!result[categoryKey]) result[categoryKey] = {};
          if (!result[categoryKey][typeKey]) result[categoryKey][typeKey] = [];

          result[categoryKey][typeKey].push({
            slot: index,
            assigned_component_ref: (assigned._id as string) || (assigned.id as string) || assigned.name,
            assigned_component: assigned,
          });
        });
      });
    });

    return result;
  }, [localShip.compiled, shipSlots]);

  const assignedCount = useMemo(() => {
    let count = 0;
    Object.values(currentLayoutSnapshot).forEach((systems) => {
      Object.values(systems).forEach((slots) => {
        count += slots.length;
      });
    });
    return count;
  }, [currentLayoutSnapshot]);

  const resetLayoutForm = () => {
    setLayoutName('');
    setLayoutDescription('');
    setIsDefaultLayout(false);
    setEditingLayoutId(null);
  };

  const handleSaveLayout = async () => {
    if (!localShip?.id) return;
    if (!layoutName.trim()) {
      showToast('El nombre del layout es obligatorio', 'warning');
      return;
    }
    if (assignedCount < 1) {
      showToast('Debes asignar al menos un componente para guardar layout', 'warning');
      return;
    }

    setSavingLayout(true);
    try {
      const payload: ComponentLayout = {
        name: layoutName.trim(),
        description: layoutDescription.trim(),
        isDefault: isDefaultLayout,
        layout: currentLayoutSnapshot,
      };

      if (editingLayoutId) {
        await garageService.updateShipLayout(localShip.id, editingLayoutId, payload);
        showToast('Layout actualizado correctamente', 'success');
      } else {
        await garageService.createShipLayout(localShip.id, payload);
        showToast('Layout guardado correctamente', 'success');
      }

      const freshLayouts = await garageService.getShipLayouts(localShip.id);
      setLayouts(freshLayouts);
      resetLayoutForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo guardar el layout';
      showToast(message, 'error');
    } finally {
      setSavingLayout(false);
    }
  };

  const applyLayoutToState = (layout: ComponentLayout, silent = false) => {
    const rawLayout = (layout.layout || {}) as Record<string, Record<string, Array<Record<string, unknown>>>>;

    setShipSlots((prevSlots) =>
      prevSlots.map((slot) => {
        const slotEntries = rawLayout?.[slot.tipoComponente]?.[slot.categoria];
        if (!Array.isArray(slotEntries)) return slot;

        const found = slotEntries.find((entry) => Number(entry.slot) === Number(slot.slotIndex));
        if (!found || !found.assigned_component) return slot;

        return {
          ...slot,
          assigned_component: found.assigned_component as PhysicalComponent,
        };
      })
    );

    setLocalShip((prev) => {
      const compiled = (prev.compiled || {}) as unknown as Record<string, Record<string, GarageComponent[]>>;
      const nextCompiled: Record<string, Record<string, GarageComponent[]>> = { ...compiled };

      Object.entries(rawLayout).forEach(([tipoComponente, categorias]) => {
        const currentSystems = nextCompiled[tipoComponente] || {};
        const nextSystems = { ...currentSystems };

        Object.entries(categorias || {}).forEach(([categoria, entries]) => {
          const currentSlots = [...(nextSystems[categoria] || [])];
          (entries || []).forEach((entry) => {
            const slotIdx = Number(entry.slot);
            if (!Number.isFinite(slotIdx) || !entry.assigned_component || !currentSlots[slotIdx]) return;
            currentSlots[slotIdx] = {
              ...currentSlots[slotIdx],
              assigned_component: entry.assigned_component as PhysicalComponent,
            };
          });
          nextSystems[categoria] = currentSlots;
        });

        nextCompiled[tipoComponente] = nextSystems;
      });

      return {
        ...prev,
        compiled: nextCompiled as unknown as CompiledSystems,
      };
    });

    if (!silent) {
      showToast(`Layout cargado: ${layout.name}`, 'success');
    }
  };

  const handleEditLayout = (layout: ComponentLayout) => {
    setEditingLayoutId((layout._id || layout.id || '') as string);
    setLayoutName(layout.name || '');
    setLayoutDescription(layout.description || '');
    setIsDefaultLayout(Boolean(layout.isDefault));
  };

  const handleApplyLayout = (layout: ComponentLayout) => {
    applyLayoutToState(layout);
  };

  const handleDeleteLayout = async (layout: ComponentLayout) => {
    if (!localShip?.id) return;
    const layoutId = (layout._id || layout.id || '') as string;
    if (!layoutId) return;
    try {
      await garageService.deleteShipLayout(localShip.id, layoutId);
      showToast('Layout eliminado correctamente', 'success');
      const freshLayouts = await garageService.getShipLayouts(localShip.id);
      setLayouts(freshLayouts);
      if (editingLayoutId === layoutId) {
        resetLayoutForm();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo eliminar el layout';
      showToast(message, 'error');
    }
  };

  useEffect(() => {
    if (!localShip?.id || layouts.length === 0) return;
    if (autoAppliedLayoutShipId === localShip.id) return;

    const preferredLayout = layouts.find((l) => l.isDefault) || layouts[0];
    if (!preferredLayout) return;

    applyLayoutToState(preferredLayout, true);
    setAutoAppliedLayoutShipId(localShip.id);
  }, [layouts, localShip?.id, autoAppliedLayoutShipId]);

  const handleSaveShipName = async () => {
    if (!localShip?.id) return;
    const normalizedNick = shipDisplayName.trim();
    if (!normalizedNick) {
      showToast('El nombre no puede estar vacío', 'warning');
      return;
    }

    setSavingNick(true);
    try {
      await shipService.updateShipNick(localShip.id, normalizedNick);
      setLocalShip((prev) => ({ ...prev, nick: normalizedNick }));
      showToast('Nombre de nave guardado', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo guardar el nombre';
      showToast(message, 'error');
    } finally {
      setSavingNick(false);
    }
  };

  const flatSlots = useMemo(() => {
    const rows: Array<{
      key: string;
      categoryKey: keyof CompiledSystems;
      typeKey: string;
      index: number;
      component: GarageComponent;
    }> = [];

    if (shipSlots.length > 0) {
      shipSlots.forEach((slot) => {
        rows.push({
          key: `${slot.tipoComponente}-${slot.categoria}-${slot.slotIndex}`,
          categoryKey: slot.tipoComponente as keyof CompiledSystems,
          typeKey: slot.categoria,
          index: slot.slotIndex,
          component: {
            ...(slot.slotData as Record<string, unknown>),
            size: slot.size,
            assigned_component: slot.assigned_component || undefined,
            name: `Slot ${COMPONENT_TYPE_NAMES[slot.categoria] || slot.categoria}`,
          } as GarageComponent,
        });
      });
      return rows;
    }

    Object.entries(localShip.compiled || {}).forEach(([categoryKey, systems]) => {
      Object.entries(systems as unknown as Record<string, GarageComponent[]>).forEach(([typeKey, components]) => {
        (components || []).forEach((component, index) => {
          rows.push({
            key: `${categoryKey}-${typeKey}-${index}`,
            categoryKey: categoryKey as keyof CompiledSystems,
            typeKey,
            index,
            component,
          });
        });
      });
    });

    return rows;
  }, [localShip.compiled, shipSlots]);

  const slotTypeCounts = useMemo(() => {
    return flatSlots.reduce<Record<string, number>>((acc, row) => {
      acc[row.typeKey] = (acc[row.typeKey] || 0) + 1;
      return acc;
    }, {});
  }, [flatSlots]);

  const nonReplaceableInfo = useMemo(() => {
    return Object.entries(slotTypeCounts)
      .filter(([typeKey]) => NON_REPLACEABLE_CATEGORIES.has(typeKey))
      .map(([typeKey, count]) => {
        const slot = flatSlots.find((s) => s.typeKey === typeKey);
        return {
          typeKey,
          count,
          size: slot ? getSlotSize(slot.component) : undefined,
        };
      });
  }, [slotTypeCounts, flatSlots]);

  const renderShipInfo = () => (
    <section className="ship-overview-panel">
      <div className="ship-image-section">
        <img
          src={localShip.imagen}
          alt={localShip.name}
          className="ship-detail-image"
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            img.src = '/media/naves/not_found.png';
          }}
        />
      </div>

      <div className="ship-info-section">
        <div className="ship-name-editor">
          <label htmlFor="ship-display-name">Nombre</label>
          <div className="ship-name-row">
            <input
              id="ship-display-name"
              type="text"
              value={shipDisplayName}
              onChange={(e) => {
                const nextName = e.target.value;
                setShipDisplayName(nextName);
                setLocalShip((prev) => ({ ...prev, nick: nextName }));
              }}
              placeholder="Nombre de la nave"
            />
            <button
              className="ship-name-save-button"
              onClick={handleSaveShipName}
              disabled={savingNick}
            >
              {savingNick ? 'Guardando...' : 'Guardar nombre'}
            </button>
          </div>
        </div>

        <div className="overview-main-grid">
          <div className="overview-description-box">
            <h4>Descripción</h4>
            <p>{localShip.description || 'Sin descripción disponible.'}</p>
          </div>
          <div className="overview-info-box">
            <h4>Info</h4>
            <p><strong>Enfoque:</strong> {localShip.focus || 'N/A'}</p>
            <p><strong>Estado:</strong> {localShip.production_status || 'N/A'}</p>
            <p><strong>Tipo:</strong> {localShip.type || 'N/A'}</p>
          </div>

          <div className="stats-grid">
            <div className="stat-item"><label>Fabricante</label><span>{localShip.manufacturer.name}</span></div>
            <div className="stat-item"><label>Tamaño</label><span>{localShip.size}</span></div>
            <div className="stat-item"><label>Tipo</label><span>{localShip.type}</span></div>
            <div className="stat-item"><label>Enfoque</label><span>{localShip.focus}</span></div>
            <div className="stat-item"><label>Estado</label><span>{localShip.production_status}</span></div>
            <div className="stat-item"><label>Tripulación</label><span>{localShip.min_crew} - {localShip.max_crew}</span></div>
            <div className="stat-item"><label>Carga</label><span>{localShip.cargo_capacity} SCU</span></div>
          </div>

          <div className="overview-info-box non-replaceable-box">
            <h4>Sistemas no sustituibles</h4>
            {nonReplaceableInfo.length === 0 ? (
              <p>Sin datos</p>
            ) : (
              nonReplaceableInfo.map((item) => (
                <p key={item.typeKey}>
                  <strong>{COMPONENT_TYPE_NAMES[item.typeKey] || item.typeKey}:</strong> {item.count} | Size {item.size ?? 'N/A'}
                </p>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );

  const renderComponents = () => {
    if (!localShip.compiled) {
      return (
        <div className="components-section">
          <h3>Configuración de Componentes</h3>
          <div className="no-components">
            <p>No hay información de componentes disponible para esta nave.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="components-section">
        <h3>Slots de Componentes</h3>
        <div className="components-layout-wrap">
          <div className="slot-list">
            {flatSlots.filter((slot) => !NON_REPLACEABLE_CATEGORIES.has(slot.typeKey)).map((slotRow) => {
              const assigned = slotRow.component.assigned_component;
              const canReplace = Boolean(normalizeComponentTypeForApi(slotRow.typeKey));
              const rowContent = (
                <>
                  <div className="slot-row-top">
                    <span className="slot-row-title">
                      Slot {COMPONENT_TYPE_NAMES[slotRow.typeKey] || slotRow.typeKey}
                    </span>
                    <span className={`slot-status-pill ${assigned ? 'is-assigned' : 'is-empty'}`}>
                      {assigned ? 'Asignado' : 'Vacío'}
                    </span>
                  </div>
                  <div className={`slot-inner-box ${assigned ? 'has-component' : ''}`}>
                    {assigned ? (
                      <>
                        <div className="slot-inner-header">
                          <div className="slot-inner-name">{assigned.name}</div>
                          <button
                            className="component-details-toggle"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleComponentDetails(`slot-${slotRow.key}`);
                            }}
                            title="Ver detalles completos"
                          >
                            <FaSearchPlus />
                          </button>
                        </div>
                        <div className="slot-inner-meta">
                          <span>{getComponentSummaryValue(assigned, 'manufacturer') || 'Fabricante N/A'}</span>
                          <span>Size {getComponentSummaryValue(assigned, 'size') || getSlotSize(slotRow.component) || 'N/A'}</span>
                          <span>Grado {getComponentSummaryValue(assigned, 'grade') || 'N/A'}</span>
                        </div>
                        {expandedComponentDetails[`slot-${slotRow.key}`] && (
                          renderComponentDetailsPanel(assigned)
                        )}
                      </>
                    ) : (
                      <div className="slot-inner-empty">
                        Sin componente asignado | Size {getSlotSize(slotRow.component) || 'N/A'}
                      </div>
                    )}
                  </div>
                </>
              );

              return (
                <button
                  key={slotRow.key}
                  className={`slot-row ${canReplace ? '' : 'slot-row-static'}`}
                  onClick={() =>
                    canReplace &&
                    handleComponentChange(
                      slotRow.categoryKey,
                      slotRow.typeKey,
                      slotRow.index,
                      slotRow.component
                    )
                  }
                  disabled={!canReplace}
                >
                  {rowContent}
                </button>
              );
            })}
          </div>

          <section className="inline-selector-section inline-selector-column">
            <div className="inline-selector-header">
              <h4>Seleccionar componente físico</h4>
              {slotContext ? (
                <button className="layout-cancel-button" onClick={closeSelector}>Cerrar</button>
              ) : null}
            </div>
            {slotContext ? (
              <p className="inline-selector-meta">
                Slot: <strong>{COMPONENT_TYPE_NAMES[slotContext.typeKey] || slotContext.typeKey}</strong> | Tamaño:{' '}
                <strong>{getSlotSize(slotContext.slot) || 'N/A'}</strong>
              </p>
            ) : (
              <p className="inline-selector-meta">Selecciona un slot para cargar componentes.</p>
            )}
            {selectorLoading ? <p>Cargando componentes...</p> : null}
            {selectorError ? <p className="error">{selectorError}</p> : null}
            {!selectorLoading && !selectorError && slotContext && (
              <div className="selector-options">
                {selectorOptions.length === 0 ? (
                  <p>No hay componentes para ese tipo/tamaño.</p>
                ) : (
                  selectorOptions.map((option, idx) => {
                    const key = (option._id || option.id || `${option.name}-${idx}`) as string;
                    return (
                      <button
                        key={key}
                        className="selector-option"
                        onClick={async () => {
                          if (!slotContext || !localShip?.id) return;
                          try {
                            await garageService.assignPhysicalComponent(
                              localShip.id,
                              slotContext.categoryKey,
                              slotContext.typeKey,
                              slotContext.index,
                              option
                            );
                            setShipSlots((prevSlots) =>
                              prevSlots.map((slot) =>
                                slot.tipoComponente === slotContext.categoryKey &&
                                slot.categoria === slotContext.typeKey &&
                                slot.slotIndex === slotContext.index
                                  ? { ...slot, assigned_component: option }
                                  : slot
                              )
                            );
                            setLocalShip((prev) => {
                              const compiled = (prev.compiled || {}) as unknown as Record<string, Record<string, GarageComponent[]>>;
                              const systems = compiled[slotContext.categoryKey as string] || {};
                              const list = [...((systems[slotContext.typeKey] || []) as GarageComponent[])];
                              if (!list[slotContext.index]) return prev;
                              list[slotContext.index] = { ...list[slotContext.index], assigned_component: option };
                              const nextSystems = { ...systems, [slotContext.typeKey]: list };
                              const nextCompiled = { ...compiled, [slotContext.categoryKey as string]: nextSystems };
                              return { ...prev, compiled: nextCompiled as unknown as CompiledSystems };
                            });
                            showToast(`Componente asignado: ${option.name}`, 'success');
                            closeSelector();
                          } catch (error) {
                            const message = error instanceof Error ? error.message : 'No se pudo asignar el componente';
                            showToast(message, 'error');
                          }
                        }}
                      >
                        <div className="selector-option-header">
                          <strong>{option.name}</strong>
                          <button
                            className="component-details-toggle"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleComponentDetails(`selector-${key}`);
                            }}
                            title="Ver detalles completos"
                          >
                            <FaSearchPlus />
                          </button>
                        </div>
                        <span>
                          {(getComponentSummaryValue(option, 'manufacturer') || 'Fabricante N/A')} | Size{' '}
                          {(getComponentSummaryValue(option, 'size') || 'N/A')} | Grado{' '}
                          {(getComponentSummaryValue(option, 'grade') || 'N/A')}
                        </span>
                        {expandedComponentDetails[`selector-${key}`] && (
                          renderComponentDetailsPanel(option)
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </section>

          <aside className="components-layout-sidebar">
            <h4>Layouts de Componentes</h4>
            <div className="layout-form">
              <input
                type="text"
                placeholder="Nombre del layout (ej: PvP agresivo)"
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
              />
              <textarea
                placeholder="Descripción (opcional)"
                value={layoutDescription}
                onChange={(e) => setLayoutDescription(e.target.value)}
                rows={3}
              />
              <label className="layout-default-checkbox">
                <input
                  type="checkbox"
                  checked={isDefaultLayout}
                  onChange={(e) => setIsDefaultLayout(e.target.checked)}
                />
                Marcar como layout por defecto
              </label>
              <div className="layout-form-actions">
                <button className="layout-save-button" onClick={handleSaveLayout} disabled={savingLayout}>
                  <FaSave />
                  {editingLayoutId ? 'Actualizar' : 'Guardar'}
                </button>
                {editingLayoutId && (
                  <button className="layout-cancel-button" onClick={resetLayoutForm}>
                    Cancelar
                  </button>
                )}
              </div>
              <p className="layout-assigned-count">
                Slots asignados: <strong>{assignedCount}</strong>
              </p>
            </div>

            <div className="layout-list">
              <h4>Guardados</h4>
              {loadingLayouts ? (
                <p>Cargando layouts...</p>
              ) : layouts.length === 0 ? (
                <p>No hay layouts guardados para esta nave.</p>
              ) : (
                layouts.map((layout) => {
                  const layoutId = (layout._id || layout.id || '') as string;
                  return (
                    <div key={layoutId} className="layout-item">
                      <div className="layout-item-header">
                        <strong>{layout.name}</strong>
                        {layout.isDefault ? <span className="layout-default-badge">Default</span> : null}
                      </div>
                      {layout.description ? <p>{layout.description}</p> : null}
                      <div className="layout-item-actions">
                        <button onClick={() => handleApplyLayout(layout)}>Cargar</button>
                        <button onClick={() => handleEditLayout(layout)}>Editar</button>
                        <button className="danger" onClick={() => handleDeleteLayout(layout)}>
                          <FaTrash />
                          Eliminar
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </aside>
        </div>
      </div>
    );
  };

  return (
    <div className="garage-repair">
      <div className="repair-header">
        <h1>{shipDisplayName || localShip.name}</h1>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <div className="repair-content">
        {renderShipInfo()}
        {renderComponents()}
      </div>
    </div>
  );
}; 